import { useTheme } from '@emotion/react';
import { Body, Box, Button, HStack, Heading, Spacer } from '@hover/blueprint';
import React, { useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import type { InferType } from 'yup';

import { BackButton } from 'src/modules/onboarding/components/BackButton';
import { OnboardingContext } from 'src/modules/onboarding/components/OnboardingContext';
import type { makePasswordValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { makeSignUpPasswordValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { PasswordInputWithValidation } from 'src/shared/components/PasswordInputWithValidation/PasswordInputWithValidation';
import {
  Form as SharedForm,
  TextField as SharedTextField,
} from 'src/shared/components/forms';
import { useAuthenticationInfoLazyQuery } from 'src/shared/graphql/generated';
import { useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';
import { baseUrlToLoginSuccessUrl } from 'src/shared/utils/url';

export type PasswordFormData = InferType<
  ReturnType<typeof makePasswordValidationSchema>
>;

const Fields = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const goBack = () => history.goBack();
  const [isInvalid, setIsInvalid] = useState(true);
  const { breakpoints } = useTheme();

  return (
    <>
      <HStack
        alignItems="center"
        flexDirection="column"
        flexGrow={1}
        justifyContent="start"
        marginTop={300}
        spacing={0}
        width={1}
      >
        <Heading
          color="text.primary"
          marginBottom="16px !important"
          size={{ base: 600, tablet: 900 }}
          textAlign={{ base: 'left', tablet: 'center' }}
          width={{ base: 1, tablet: '400px' }}
        >
          {t('onboarding.professionalAccount')}
        </Heading>
        <Body
          as={Box}
          color="neutralText"
          justifyContent={{ base: 'start', tablet: 'center' }}
          opacity={0.64}
          size="small"
          width={{ base: 1, tablet: '500px' }}
        >
          {t('onboarding.weJustNeedAFewPieces')}
        </Body>
        <Box
          flexDirection="column"
          flexGrow={1}
          width={{ base: 1, tablet: '500px' }}
        >
          <SharedTextField
            id="email"
            label={t('onboarding.email')}
            name="email"
            paddingTop={500}
            placeholder=""
            type="email"
          />
          <Box flexDir="column">
            <PasswordInputWithValidation setIsInvalid={setIsInvalid} />
          </Box>
          <Spacer flex={{ base: 1, tablet: 0 }} />
          <Box>
            <Button
              disabled={isInvalid}
              mb={400}
              mt={300}
              onClick={() => {
                buttonPressed({
                  button_text: 'Next',
                  page_or_screen_name: 'Onboarding-signup-email_input',
                  feature: 'onboarding',
                });
              }}
              size="large"
              type="submit"
              width={1}
            >
              {t('onboarding.createAccount')}
            </Button>
          </Box>
        </Box>
      </HStack>
      <Box
        alignItems="center"
        display={{ base: 'none', tablet: 'flex' }}
        flexDirection="column"
        width={1}
      >
        <Box
          justifyContent="start"
          maxW={breakpoints.width500}
          paddingBottom={300}
          paddingLeft={400}
          width={1}
        >
          <BackButton onClick={goBack} />
        </Box>
      </Box>
    </>
  );
};

export const SignupPro = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const { pathname, search } = useLocation();
  const [queryAuthenticationInfo] = useAuthenticationInfoLazyQuery();

  const [contextState, dispatch] = useContext(OnboardingContext);

  const validationSchema = makeSignUpPasswordValidationSchema(t);
  const initialValues = { email: contextState.email, password: '' };

  const onSubmit = async (formData: PasswordFormData) => {
    const { email, password } = formData;
    const orgType = pathname === '/onboarding/pro' ? 'pro' : 'insurance';

    if (!password || !email) return;

    buttonPressed({
      button_text: 'Create account',
      page_or_screen_name: 'Email_input_password',
      email,
      org_type: orgType,
      feature: 'onboarding',
    });

    const redirectPath = baseUrlToLoginSuccessUrl(window.location.href);

    const result = await queryAuthenticationInfo({
      variables: {
        email,
        redirectPath,
      },
    });

    const authenticationInfo = result?.data?.authenticationInfo;
    const { ssoEnabled, ssoSpSigninUrl, ssoStrictModeEnabled } =
      authenticationInfo || {};

    if (ssoEnabled && ssoSpSigninUrl && ssoStrictModeEnabled) {
      window.location.href = ssoSpSigninUrl;
    } else {
      dispatch({ email, password, orgType });

      history.push(`${Routes.SIGN_UP_ONBOARDING.path}/personal-info${search}`);
    }
  };

  return (
    <Box alignItems="stretch" flex={1} flexDirection="column" width={1}>
      <SharedForm<PasswordFormData>
        display="flex"
        flexGrow={1}
        initialValues={initialValues}
        marginX={500}
        marginY={{ base: 0, tablet: 400 }}
        onSubmit={onSubmit}
        transparentFieldset
        validationSchema={validationSchema}
      >
        <Fields />
      </SharedForm>
    </Box>
  );
};
